import React, { useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  Typography
} from '@material-ui/core';
import { setImportGeoValidationResult, setImportDupCheckErrors, revalidateImportRows, setImportGridEditDirty } from 'store/actions';
import { importTypeStateSelector } from 'store/selectors';
import classNames from 'classnames';

import DataGrid, {
  Column,
  Pager,
  Paging,
  Sorting,
  Scrolling,
  ColumnFixing,
  Editing,
  KeyboardNavigation,
  Lookup
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import { Constants } from 'utilities/constants';

const ImportPropertiesGrid = React.forwardRef(({ classes, geoValidationResult, dupCheckErrors, importRows,
  setImportGeoValidationResult, setImportDupCheckErrors, revalidateImportRows, setImportGridEditDirty,
  setAddressToVerify, setOpenLocationVerification, setContextRowKey, appSettings }, gridRef) => {
  const invalidZipCodes = geoValidationResult.invalidZipCodes || [];
  const invalidCounties = geoValidationResult.invalidCounties || [];
  const invalidGeo = geoValidationResult.invalidGeo || [];
  const dupRowReferenceIds = dupCheckErrors?.map(error => error.referenceId) || [];
  const hasErrors = importRows.some(row => row.invalidColumns?.length > 0) || invalidZipCodes.length || invalidCounties.length || invalidGeo.length || dupRowReferenceIds.length;
  const maxCountExceeded = importRows?.length > Constants.ImportMaxCount.PROPERTIES;

  useEffect(() => {
    const dataSource = gridRef.current.instance.getDataSource();
    if (!(dataSource && importRows)) return;
    if (gridRef.current.instance.hasEditData()) {
      gridRef.current.instance.cancelEditData();
    }
  }, [importRows])

  const onFocusedCellChanging = e => {
    e.isHighlighted = true;
  };

  const onContentReady = e => {
    setImportGridEditDirty(gridRef.current.instance.hasEditData());
    if (!importRows || !hasErrors) return;

    importRows.forEach((row, index) => {
      const rowIndex = gridRef.current.instance.getRowIndexByKey(row.referenceId);
      if (rowIndex === -1) return;

      if (geoValidationResult.invalidZipCodes?.includes(row.zipCode)) { // Mark cells that failed server zip code validation
        const cell = gridRef.current.instance.getCellElement(rowIndex, 'zipCode');
        if (cell) {
          cell.classList.add('invalidCell');
        }
      }
      if (geoValidationResult.invalidCounties?.includes(row.referenceId)) { // Mark cells that failed server county validation
        const cell = gridRef.current.instance.getCellElement(rowIndex, 'county');
        if (cell) {
          cell.classList.add('invalidCell');
        }
      }
      if (geoValidationResult.invalidGeo?.includes(row.referenceId)) { // Mark cells that has no lat lng
        const rowEl = gridRef.current.instance.getRowElement(rowIndex);
        if (rowEl?.[0]) {
          rowEl[0].classList.add('invalidCell');
        }
        if (rowEl?.[1]) {
          rowEl[1].classList.add('invalidCell');
        }
      }
      if (dupRowReferenceIds?.includes(row.referenceId)) { // Mark rows that are dups
        const rowEl = gridRef.current.instance.getRowElement(rowIndex);
        if (rowEl?.[0]) {
          rowEl[0].classList.add('invalidCell');
        }
        if (rowEl?.[1]) {
          rowEl[1].classList.add('invalidCell');
        }
      }

      if (row.invalidColumns?.length) {
        row.invalidColumns.forEach(col => {
          let fields;
          if (col.name === 'address') {
            fields = ['street', 'city', 'state', 'zipCode'];
          } else {
            fields = [col.name]
          }
          fields.forEach(field => {
            const cell = gridRef.current.instance.getCellElement(rowIndex, field);
            if (cell) {
              cell.classList.add('invalidCell');
            }
          })
        })
      }
    })
  }

  //#region CONTEXT MENU region
  const handleContextMenu = e => {
    if (e.target === 'content') {
      // e.items can be undefined
      if (!e.items) e.items = [];
      if (e.row) {
        const { rowType } = e.row;
        if (rowType === 'data') {
          setContextRowKey(e.row.key);
          e.items = getImportContextItems(e);
        }
      }
    }
  }

  function getImportContextItems(e) {
    const items = [];
    if (geoValidationResult?.geoValidationDtos) {
      const geoValidationDto = geoValidationResult.geoValidationDtos.find(dto => dto.referenceId === e.row.data.referenceId);
      items.push({
        text: `Verify Location`,
        onItemClick: () => {
          setAddressToVerify({
            ...geoValidationDto,
            city: e.row.data.city, // Stitch in city, state, lat, long from grid, since we don't pass this in geoValidationDto
            state: e.row.data.state,
            latitude: e.row.data.latitude,
            longitude: e.row.data.longitude
          });
          setOpenLocationVerification(true);
        },
      });
    }

    return items;
  }

  function getHeaderCell(el, headerStyle) {
    return (
      <div>
        <span className={headerStyle.autofill ? classes.autofillField : null}>{el.data.column.caption}</span>
        {headerStyle.required &&
          <span className={classes.requiredField}>*</span>
        }
      </div>
    )
  }

  const onToolbarPreparing = e => {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        template: 'totalCount'
      },
      {
        location: 'before',
        template: 'excludedCount'
      },
      {
        location: 'before',
        template: 'columnKey'
      },
    );

    const saveButton = e.toolbarOptions.items.find(el => el.name === 'saveButton');
    if (saveButton) {
      saveButton.options.onClick = handleSaveEdit;
    }
  }

  const toolbarItemRender = (e) => {
    switch (e.template) {
      case 'totalCount':
        return totalCountTemplate();
      case 'excludedCount':
        return excludedCountTemplate();
      case 'columnKey':
        return columnKeyTemplate();
      default:
        return;
    }
  }

  const totalCountTemplate = (e) => {
    return (<div className={classNames(classes.informer, maxCountExceeded && classes.errorInformer)}>
      <span className={classes.countLabel}>{importRows?.length ?? 0}</span>
      <span className={classes.totalCountText}>Total Count</span>
    </div>)
  }

  const excludedCountTemplate = (e) => {
    const totalCount = gridRef?.current?.instance?.totalCount();
    const filter = gridRef?.current?.instance?.getCombinedFilter();
    return totalCount && filter
      ? <div className={classNames(classes.informer, classes.exclusionInformer)}>
        <span className={classes.countLabel}>{totalCount > -1 ? totalCount : 0}</span>
        <span className={classes.totalCountText}>Visible</span>
      </div>
      : null
  }

  const columnKeyTemplate = (e) => {
    return (<div style={{ padding: 4 }}>
      <Typography variant='body2'><span style={{ color: 'red' }}>*</span> Required field</Typography>
      <Typography variant='body2'><span style={{ fontWeight: 700 }}>Autofill field</span></Typography>
    </div>)
  }

  const handleSaveEdit = () => {
    const geoValidationFailed = geoValidationResult.passedValidation === false;
    const dupFound = dupCheckErrors?.length > 0;
    gridRef.current.instance.saveEditData().then(() => {
      const dataSource = gridRef.current.instance.getDataSource();
      gridRef.current.instance.beginUpdate();
      gridRef.current.instance.clearFilter();
      setTimeout(() => { // Important - as grid internally rerenders from above clearFilter(), items() will return empty array. Add delay here to get correct items array.
        const items = [...dataSource.items()];
        if (geoValidationFailed || dupFound) {
          checkForDeleted(items);
        }
        revalidateImportRows(items);
        gridRef.current.instance.endUpdate();
      }, 100);
    });
  }

  const checkForDeleted = (dataSourceItems) => {
    if (importRows?.length === dataSourceItems?.length) return;
    const deletedRows = importRows.filter(oldRow => !dataSourceItems.find(newRow => newRow.referenceId === oldRow.referenceId));
    if (!deletedRows.length) return;

    const deletedKeys = deletedRows.map(row => row.referenceId);

    const newInvalidZipcodes = invalidZipCodes.filter(invalidZip => dataSourceItems.some(row => row.zipCode === invalidZip));
    const newInvalidCounties = invalidCounties.filter(oldKey => !deletedKeys.includes(oldKey));
    const newInvalidGeo = invalidGeo.filter(oldKey => !deletedKeys.includes(oldKey));
    const newDupCheckErrors = dupCheckErrors.filter(oldError => !deletedKeys.includes(oldError.referenceId));
    const newGeoValidationDtos = geoValidationResult.geoValidationDtos.filter(dto => !deletedKeys.includes(dto.referenceId))

    const newPassedValidation = !newInvalidZipcodes.length && !newInvalidCounties.length && !newInvalidGeo.length;
    setImportGeoValidationResult({
      passedValidation: newPassedValidation,
      invalidZipCodes: newInvalidZipcodes,
      invalidCounties: newInvalidCounties,
      invalidGeo: newInvalidGeo,
      geoValidationDtos: newGeoValidationDtos
    });
    setImportDupCheckErrors(newDupCheckErrors);
  }

  return (
    <DataGrid
      id='exploreImportGrid' // Must match in ReVUeSetting.json for tooltips to work
      ref={gridRef}
      dataSource={importRows.map(row => { return { ...row } })} // Create array of new rows, else deleting rows/editing cells will mutate the state
      keyExpr='referenceId'
      allowColumnResizing={true}
      columnResizingMode='widget'
      showBorders={true}
      showRowLines={true}
      showColumnLines={true}
      height={'100%'}
      // hoverStateEnabled={true}
      focusedRowEnabled={false}
      onContextMenuPreparing={handleContextMenu}
      // loadPanel={{ enabled: false }}
      onContentReady={onContentReady}
      onFocusedCellChanging={onFocusedCellChanging}
      onToolbarPreparing={onToolbarPreparing}
    >
      <Template name='totalCount' render={toolbarItemRender} />
      <Template name='excludedCount' render={toolbarItemRender} />
      <Template name='columnKey' render={toolbarItemRender} />
      <Editing
        mode='batch'
        allowUpdating={true}
        allowDeleting={true}
        // selectTextOnEditStart={true}
        startEditAction='dblClick'
      />
      <KeyboardNavigation
        editOnKeyPress={true}
        enterKeyAction='startEdit'
        enterKeyDirection={'column'}
      />
      <ColumnFixing enable={true} />
      <Sorting mode='none' />
      <Scrolling rowRenderingMode='virtual' columnRenderingMode='virtual'/>
      <Paging
        defaultPageSize={100}
        enabled={false}
      />
      <Pager showInfo={true} />
      {appSettings.FieldSettings.filter(field => field.exploreImportGrid).map(({ field, displayName, width, type, format, exploreImportGrid }) => {
        const { fixed, visible, required, autofill, acceptedValues } = exploreImportGrid;
        const index = appSettings.FieldIndex.exploreImportGrid[field];
        const headerStyle = {
          autofill,
          required
        }
        let lookupDataSource = null;
        if (type === 'boolean') {
          lookupDataSource = [{ displayExpr: 'true', valueExpr: true }, { displayExpr: 'false', valueExpr: false }];
        } else if (!!acceptedValues) {
          lookupDataSource = acceptedValues.map(value => {
            return {
              displayExpr: value,
              valueExpr: value
            }
          });
        }
        if (lookupDataSource && !required) { // Add in null lookup option if field not required
          lookupDataSource.unshift({ displayExpr: '', valueExpr: null });
        }
        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            fixed={fixed || false}
            minWidth={width}
            dataType={type === 'date' ? 'string' : type} // display dates as strings (cell editing has errors when using dates)
            format={format}
            allowExporting={true}
            visible={visible === false ? false : true}
            headerCellComponent={headerStyle ? (el) => getHeaderCell(el, headerStyle) : undefined}
            visibleIndex={index}
            showEditorAlways={type === 'boolean' || !!acceptedValues}
          >
            {!!lookupDataSource &&
              <Lookup dataSource={lookupDataSource} displayExpr="displayExpr" valueExpr="valueExpr" />
            }
          </Column>
        )
      })}
    </DataGrid>
  )
})

const styles = theme => ({
  requiredField: {
    color: 'red'
  },
  autofillField: {
    fontWeight: '800'
  },
  informer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#80a4c7',
    color: '#fff',
    textAlign: 'center'
  },
  exclusionInformer: {
    background: '#67c12e',
  },
  errorInformer: {
    background: '#d32f2f'
  },
  countLabel: {
    fontSize: '18px',
    paddingTop: '5px',
    margin: '0'
  },
  totalCountText: {
    paddingBottom: '5px',
  },
});

const mapStateToProps = state => {
  const propertiesState = importTypeStateSelector(state, Constants.ImportType.PROPERTIES);
  return {
    importRows: propertiesState.importRows,
    geoValidationResult: propertiesState.importGeoValidationResult,
    dupCheckErrors: propertiesState.importDupCheckErrors,
    appSettings: state.appState.appSettings
  }
}
const mapDispatchToProps = dispatch => {
  const importType = Constants.ImportType.PROPERTIES;
  return {
    setImportGeoValidationResult: (importGeoValidationResult) => dispatch(setImportGeoValidationResult({ importType, importGeoValidationResult })),
    setImportDupCheckErrors: (dupCheckErrors) => dispatch(setImportDupCheckErrors({ importType, dupCheckErrors })),
    revalidateImportRows: (rows) => dispatch(revalidateImportRows({ importType, rows })),
    setImportGridEditDirty: (gridEditDirty) => dispatch(setImportGridEditDirty({ importType, gridEditDirty }))
  }
};
export default compose(withStyles(styles), connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { forwardRef: true }
))(ImportPropertiesGrid);
